import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  checkAvailableFirmware,
  deleteChangeLog,
  deleteFirmware,
  deleteReleaseNote,
  getChangeLog,
  getDeviceDataById,
  getFirmware,
  getReleaseNote,
  listChangeLog,
  listFirmwares,
  listPromotions,
  requestFirmwareUpgrade,
  upsertChangeLog,
  upsertReleaseNote,
  uploadFirmware,
} from '../../api/firmware';
import { useAuth } from '../../auth/AuthContext';
import FirmwareBatchRolloutsTab from './components/FirmwareBatchRolloutsTab';
import FirmwareDeviceUpdatesTab from './components/FirmwareDeviceUpdatesTab';
import FirmwarePackagesTab from './components/FirmwarePackagesTab';
import FirmwareGroupsDialog from './components/FirmwareGroupsDialog';
import FirmwarePromotionDialog from './components/FirmwarePromotionDialog';
import {
  BatchDetailsDialog,
  FirmwareDetailsDialog,
  InstallFirmwareDialog,
  NewBatchRolloutDialog,
  RemoveFirmwareDialog,
  UploadFirmwareDialog,
} from './components/FirmwareDialogs';
import {
  DEVICE_WRITE_ROLES,
  INITIAL_BATCH_FORM,
  INITIAL_UPLOAD_FORM,
  REMOVE_ROLES,
  RELEASE_ROLES,
  UPLOAD_ROLES,
  VIEW_ROLES,
} from './firmwareConstants';
import { INITIAL_BATCHES } from './firmwareMockData';
import { firmwareChannels, formatDate, hasRequiredValues } from './firmwareUtils';
import './firmware.css';

function toPackageRecord(firmware) {
  return {
    ...firmware,
    releaseDate: formatDate(firmware.releaseDate || firmware.createdAt),
    summary: 'No release notes are available for this firmware package.',
  };
}

export default function FirmwarePage({ initialDevice = null }) {
  const { tokens, user } = useAuth();
  const idToken = tokens?.idToken;
  const activeRole = user?.activeRole;
  const canView = VIEW_ROLES.includes(activeRole);
  const canUpload = UPLOAD_ROLES.includes(activeRole);
  const canRelease = RELEASE_ROLES.includes(activeRole);
  const canRemove = REMOVE_ROLES.includes(activeRole);
  const canRequestDeviceUpdate = DEVICE_WRITE_ROLES.includes(activeRole);
  const initialDeviceId = initialDevice?.deviceId;
  const initialChildDeviceId = initialDevice?.childDeviceId;
  const initialRegionId = initialDevice?.regionId;

  const [activeTab, setActiveTab] = useState(initialDevice ? 'devices' : 'packages');
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [packagesError, setPackagesError] = useState('');
  const [packagesNextToken, setPackagesNextToken] = useState(null);
  const [packageSearch, setPackageSearch] = useState('');
  const [packageChannel, setPackageChannel] = useState('ALL');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageDetailsLoading, setPackageDetailsLoading] = useState(false);
  const [packageDetailsError, setPackageDetailsError] = useState('');
  const [packageDetailsReload, setPackageDetailsReload] = useState(0);
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [releaseNotesLoading, setReleaseNotesLoading] = useState(false);
  const [releaseNotesError, setReleaseNotesError] = useState('');
  const [releaseNoteLoadingLanguage, setReleaseNoteLoadingLanguage] = useState('');
  const [releaseNoteSaving, setReleaseNoteSaving] = useState(false);
  const [releaseNoteDeletingLanguage, setReleaseNoteDeletingLanguage] = useState('');
  const [changeLogs, setChangeLogs] = useState([]);
  const [changeLogsLoading, setChangeLogsLoading] = useState(false);
  const [changeLogsError, setChangeLogsError] = useState('');
  const [changeLogLoadingLanguage, setChangeLogLoadingLanguage] = useState('');
  const [changeLogSaving, setChangeLogSaving] = useState(false);
  const [changeLogDeletingLanguage, setChangeLogDeletingLanguage] = useState('');
  const [promotionPackage, setPromotionPackage] = useState(null);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [removalPackage, setRemovalPackage] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState(INITIAL_UPLOAD_FORM);
  const [firmwareFile, setFirmwareFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [deviceQuery, setDeviceQuery] = useState(initialDevice?.deviceId || '');
  const [childDeviceId, setChildDeviceId] = useState(initialDevice?.childDeviceId || '');
  const [deviceRegionId, setDeviceRegionId] = useState(initialDevice?.regionId || 'EU_CENTRAL_1');
  const [deviceSearchPerformed, setDeviceSearchPerformed] = useState(false);
  const [searchingDevice, setSearchingDevice] = useState(false);
  const [deviceSearchError, setDeviceSearchError] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceChannel, setDeviceChannel] = useState('');
  const [checking, setChecking] = useState(false);
  const compatibilityRequest = useRef(0);
  const deviceRequest = useRef(0);
  const [availableFirmware, setAvailableFirmware] = useState([]);
  const [upgradeError, setUpgradeError] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [upgradeResult, setUpgradeResult] = useState([]);
  const [installOpen, setInstallOpen] = useState(false);
  const [installReason, setInstallReason] = useState('');
  const [batches, setBatches] = useState(INITIAL_BATCHES);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchForm, setBatchForm] = useState(INITIAL_BATCH_FORM);
  const [batchDetails, setBatchDetails] = useState(null);
  const [notice, setNotice] = useState('');

  useEffect(() => () => {
    compatibilityRequest.current += 1;
    deviceRequest.current += 1;
  }, [idToken]);

  const loadPackages = useCallback(async ({ nextToken = '', append = false } = {}) => {
    try {
      setPackagesLoading(true);
      setPackagesError('');
      const result = await listFirmwares(idToken, {
        limit: 10,
        nextToken,
      });
      const loadedPackages = result.items.map(toPackageRecord);
      setPackages((current) => (append ? [...current, ...loadedPackages] : loadedPackages));
      setPackagesNextToken(result.nextToken);
    } catch (error) {
      setPackagesError(error instanceof Error ? error.message : String(error));
    } finally {
      setPackagesLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    void loadPackages();
  }, [loadPackages]);

  const retryChannels = async () => {
    setPackagesLoading(true);
    try {
      for (const firmware of packages.filter((item) => item.promotionsError)) {
        try {
          const promotions = await listPromotions(idToken, { firmwareId: firmware.id });
          setPackages((current) => current.map((item) => item.id === firmware.id ? { ...item, promotions, promotionsError: '' } : item));
        } catch (error) {
          setPackages((current) => current.map((item) => item.id === firmware.id ? { ...item, promotionsError: error instanceof Error ? error.message : String(error) } : item));
        }
      }
    } finally {
      setPackagesLoading(false);
    }
  };

  const detailId = selectedPackage?.id;
  useEffect(() => {
    if (!detailId) return;
    let active = true;
    setPackageDetailsLoading(true);
    setPackageDetailsError('');
    setReleaseNotes([]);
    setReleaseNotesError('');
    setReleaseNotesLoading(true);
    setChangeLogs([]);
    setChangeLogsError('');
    setChangeLogsLoading(true);

    void getFirmware(idToken, { id: detailId })
      .then((firmware) => {
        if (!active) return;
        setSelectedPackage(toPackageRecord(firmware));
        setReleaseNotes(firmware.releaseNotes);
      })
      .catch((error) => { if (active) setPackageDetailsError(error instanceof Error ? error.message : String(error)); })
      .finally(() => { if (active) { setPackageDetailsLoading(false); setReleaseNotesLoading(false); } });

    // Changelogs are not included in getFirmware and remain a separate request.
    void listChangeLog(idToken, { firmwareId: detailId })
      .then((logs) => { if (active) setChangeLogs(logs); })
      .catch((error) => { if (active) setChangeLogsError(error instanceof Error ? error.message : String(error)); })
      .finally(() => { if (active) setChangeLogsLoading(false); });
    return () => { active = false; };
  }, [detailId, idToken, packageDetailsReload]);

  const filteredPackages = useMemo(() => packages.filter((item) => {
    const term = packageSearch.trim().toLowerCase();
    const matchesText = !term || [item.deviceModel, item.hardwareVersion, item.firmwareVersion, item.id]
      .some((value) => value.toLowerCase().includes(term));
    const matchesChannel = packageChannel === 'ALL' || firmwareChannels(item).includes(packageChannel);
    return matchesText && matchesChannel;
  }), [packageChannel, packageSearch, packages]);

  const compatibleUpdate = useMemo(
    () => availableFirmware.find((item) => item?.update && item?.version && firmwareChannels(item).includes(deviceChannel)) || null,
    [availableFirmware, deviceChannel],
  );
  const batchPackage = packages.find((item) => item.id === batchForm.firmwareId);
  const batchCount = Number(batchForm.firstWave || 0) + Number(batchForm.secondWave || 0) + Number(batchForm.thirdWave || 0);

  const updateUploadForm = (field) => (event) => {
    setUploadError('');
    setUploadForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const submitUpload = async (event) => {
    event.preventDefault();
    if (!firmwareFile) {
      setUploadError('Select a firmware file to upload.');
      return;
    }
    if (!hasRequiredValues(uploadForm, ['deviceModel', 'hardwareVersion', 'firmwareVersion'])) {
      setUploadError('Device model, hardware version, and firmware version are required.');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');
      const firmware = await uploadFirmware(tokens?.idToken, { file: firmwareFile, ...uploadForm });
      const packageRecord = {
        id: firmware.id,
        deviceModel: firmware?.deviceModel || uploadForm.deviceModel,
        hardwareVersion: firmware?.hardwareVersion || uploadForm.hardwareVersion,
        firmwareVersion: firmware?.firmwareVersion || uploadForm.firmwareVersion,
        promotions: firmware.promotions || [],
        promotionsError: firmware.promotionsError || '',
        sizeInBytes: firmware?.sizeInBytes || firmwareFile.size,
        releaseDate: firmware?.createdAt ? formatDate(firmware.createdAt) : 'Just now',
        createdAt: firmware?.createdAt || new Date().toISOString(),
        summary: 'New firmware package.',
      };
      setPackages((current) => [packageRecord, ...current]);
      setFirmwareFile(null);
      setUploadForm(INITIAL_UPLOAD_FORM);
      setUploadOpen(false);
      setNotice(`${packageRecord.firmwareVersion} was uploaded and added to the package catalogue.`);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : String(error));
    } finally {
      setUploading(false);
    }
  };

  const loadDevice = useCallback(async ({ deviceId, childDeviceId: childId, regionId }) => {
    if (!deviceId || !childId) {
      setDeviceSearchError('Device ID and child device ID are required.');
      return;
    }

    const requestId = ++deviceRequest.current;
    try {
      setSearchingDevice(true);
      compatibilityRequest.current += 1;
      setChecking(false);
      setDeviceSearchPerformed(false);
      setDeviceSearchError('');
      setUpgradeError('');
      setAvailableFirmware([]);
      setDeviceChannel('');
      setInstallOpen(false);
      setUpgradeResult([]);
      const result = await getDeviceDataById(idToken, {
        deviceId,
        childDeviceId: childId,
        regionId,
      });
      if (requestId !== deviceRequest.current) return;
      const device = result.deviceData;
      const deviceModel = device.interface?.model || device.manufacturer?.model || device.deviceType || '';
      const hardwareVersion = device.interface?.hardwareVersion || device.hardwareVersion || '';
      const operatorFirmwareVersion = device.firmwareVersion || '';
      const interfaceFirmwareVersion = device.interface?.firmwareVersion || '';
      const currentVersion = interfaceFirmwareVersion;
      setSelectedDevice({
        id: device.deviceId || deviceId,
        childDeviceId: device.childDeviceId || childId,
        name: device.deviceName || device.deviceType || deviceId,
        location: [device.location?.country, device.location?.timezone?.id].filter(Boolean).join(' / ') || 'Location not available',
        regionId: String(result.region || 'EU_CENTRAL_1').toUpperCase().replace(/-/g, '_'),
        deviceModel,
        hardwareVersion,
        operatorFirmwareVersion,
        interfaceFirmwareVersion,
        currentVersion,
        status: device.connectionStatus || 'Unknown',
        serialNumber: device.manufacturer?.serialNumber || device.interface?.serialNumber || 'Not available',
        lastSeen: device.updatedAt ? formatDate(device.updatedAt) : 'Not available',
      });
      setDeviceSearchPerformed(true);
    } catch (error) {
      if (requestId !== deviceRequest.current) return;
      setSelectedDevice(null);
      setDeviceSearchPerformed(true);
      setDeviceSearchError(error instanceof Error ? error.message : String(error));
    } finally {
      if (requestId === deviceRequest.current) setSearchingDevice(false);
    }
  }, [idToken]);

  useEffect(() => {
    if (canView && initialDeviceId && initialChildDeviceId) {
      void loadDevice({ deviceId: initialDeviceId, childDeviceId: initialChildDeviceId, regionId: initialRegionId });
    }
  }, [canView, initialDeviceId, initialChildDeviceId, initialRegionId, loadDevice]);

  const searchDevice = (event) => {
    event.preventDefault();
    void loadDevice({ deviceId: deviceQuery.trim(), childDeviceId: childDeviceId.trim(), regionId: deviceRegionId });
  };

  const changeDeviceChannel = (channel) => {
    setDeviceChannel(channel);
    setUpgradeError('');
    setInstallOpen(false);
    setUpgradeResult([]);
  };

  const checkCompatibility = async () => {
    if (!selectedDevice?.deviceModel || !selectedDevice.hardwareVersion || !selectedDevice.currentVersion) {
      setUpgradeError('The device response is missing a firmware model, hardware version, or interface firmware version.');
      return;
    }

    const requestId = ++compatibilityRequest.current;
    try {
      setChecking(true);
      setAvailableFirmware([]);
      setDeviceChannel('');
      setInstallOpen(false);
      setUpgradeError('');
      setUpgradeResult([]);
      const result = await checkAvailableFirmware(tokens?.idToken, {
        regionId: selectedDevice.regionId,
        deviceModel: selectedDevice.deviceModel,
        hardwareVersion: selectedDevice.hardwareVersion,
        currentVersion: selectedDevice.currentVersion,
      });
      if (requestId !== compatibilityRequest.current) return;
      setAvailableFirmware(result);
      const update = result.find((item) => item?.update && item?.version);
      const channels = update ? firmwareChannels(update) : [];
      setDeviceChannel(channels.includes('PRODUCTION') ? 'PRODUCTION' : channels[0] || '');
    } catch (error) {
      if (requestId === compatibilityRequest.current) setUpgradeError(error instanceof Error ? error.message : String(error));
    } finally {
      if (requestId === compatibilityRequest.current) setChecking(false);
    }
  };

  const requestUpgrade = async () => {
    if (!installReason.trim()) {
      setUpgradeError('A support reason or ticket reference is required.');
      return;
    }
    if (!compatibleUpdate || !selectedDevice) {
      setUpgradeError('Check compatibility and select a channel with an eligible firmware update first.');
      return;
    }

    try {
      setRequesting(true);
      setUpgradeError('');
      const result = await requestFirmwareUpgrade(tokens?.idToken, {
        regionId: selectedDevice.regionId,
        hardwareVersion: selectedDevice.hardwareVersion,
        currentVersion: selectedDevice.currentVersion,
        channel: deviceChannel,
        deviceId: selectedDevice.id,
        childDeviceId: selectedDevice.childDeviceId,
      });
      const submittedUpdate = result.find((item) => item?.update);
      if (!submittedUpdate) {
        throw new Error('No eligible firmware update was accepted for this device. Check compatibility again before retrying.');
      }
      setUpgradeResult(result);
      setInstallOpen(false);
      setNotice(`${submittedUpdate.version || 'Firmware update'} was requested for ${selectedDevice.name}.`);
    } catch (error) {
      setUpgradeError(error instanceof Error ? error.message : String(error));
    } finally {
      setRequesting(false);
    }
  };

  const removePackage = async () => {
    if (!removalPackage) return;

    try {
      setRemoving(true);
      setRemoveError('');
      await deleteFirmware(tokens?.idToken, {
        deviceModel: removalPackage.deviceModel,
        hardwareVersion: removalPackage.hardwareVersion,
        firmwareVersion: removalPackage.firmwareVersion,
      });
      setPackages((current) => current.filter((item) => item.id !== removalPackage.id));
      setNotice(`${removalPackage.firmwareVersion} was removed from the catalogue.`);
      setRemovalPackage(null);
    } catch (error) {
      setRemoveError(error instanceof Error ? error.message : String(error));
    } finally {
      setRemoving(false);
    }
  };

  const openPackageDetails = (firmware) => {
    setSelectedPackage(firmware);
    setPackageDetailsLoading(true);
    setPackageDetailsError('');
    setReleaseNotes([]);
    setReleaseNotesError('');
    setChangeLogs([]);
    setChangeLogsError('');

  };

  const loadReleaseNote = async (language) => {
    if (!selectedPackage) return null;

    try {
      setReleaseNoteLoadingLanguage(language);
      setReleaseNotesError('');
      const releaseNote = await getReleaseNote(idToken, {
        firmwareId: selectedPackage.id,
        language,
      });
      if (releaseNote) {
        setReleaseNotes((current) => current.map((note) => (
          note.language === language ? releaseNote : note
        )));
      }
      return releaseNote;
    } catch (error) {
      setReleaseNotesError(error instanceof Error ? error.message : String(error));
      throw error;
    } finally {
      setReleaseNoteLoadingLanguage('');
    }
  };

  const saveReleaseNote = async ({ language, title, body }) => {
    if (!selectedPackage) return null;

    try {
      setReleaseNoteSaving(true);
      const releaseNote = await upsertReleaseNote(idToken, {
        firmwareId: selectedPackage.id,
        language,
        title,
        body,
      });
      setReleaseNotes((current) => {
        const remaining = current.filter((note) => note.language !== releaseNote.language);
        return [...remaining, releaseNote].sort((left, right) => left.language.localeCompare(right.language));
      });
      setNotice(`Release note (${releaseNote.language.toUpperCase()}) was saved.`);
      return releaseNote;
    } finally {
      setReleaseNoteSaving(false);
    }
  };

  const removeReleaseNote = async (language) => {
    if (!selectedPackage) return null;

    try {
      setReleaseNoteDeletingLanguage(language);
      await deleteReleaseNote(idToken, {
        firmwareId: selectedPackage.id,
        language,
      });
      setReleaseNotes((current) => current.filter((note) => note.language !== language));
      setNotice(`Release note (${language.toUpperCase()}) was deleted.`);
    } finally {
      setReleaseNoteDeletingLanguage('');
    }
  };

  const loadChangeLog = async (language) => {
    if (!selectedPackage) return null;

    try {
      setChangeLogLoadingLanguage(language);
      setChangeLogsError('');
      const changeLog = await getChangeLog(idToken, {
        firmwareId: selectedPackage.id,
        language,
      });
      if (changeLog) {
        setChangeLogs((current) => current.map((entry) => (
          entry.language === language ? changeLog : entry
        )));
      }
      return changeLog;
    } catch (error) {
      setChangeLogsError(error instanceof Error ? error.message : String(error));
      throw error;
    } finally {
      setChangeLogLoadingLanguage('');
    }
  };

  const saveChangeLog = async ({ language, title, body }) => {
    if (!selectedPackage) return null;

    try {
      setChangeLogSaving(true);
      const changeLog = await upsertChangeLog(idToken, {
        firmwareId: selectedPackage.id,
        language,
        title,
        body,
      });
      setChangeLogs((current) => {
        const remaining = current.filter((entry) => entry.language !== changeLog.language);
        return [...remaining, changeLog].sort((left, right) => left.language.localeCompare(right.language));
      });
      setNotice(`Changelog (${changeLog.language.toUpperCase()}) was saved.`);
      return changeLog;
    } finally {
      setChangeLogSaving(false);
    }
  };

  const removeChangeLog = async (language) => {
    if (!selectedPackage) return null;

    try {
      setChangeLogDeletingLanguage(language);
      await deleteChangeLog(idToken, {
        firmwareId: selectedPackage.id,
        language,
      });
      setChangeLogs((current) => current.filter((entry) => entry.language !== language));
      setNotice(`Changelog (${language.toUpperCase()}) was deleted.`);
    } finally {
      setChangeLogDeletingLanguage('');
    }
  };

  const createBatch = (event) => {
    event.preventDefault();
    if (!batchForm.ticket.trim()) return;
    const newBatch = {
      id: `BATCH-${batchForm.region === 'EU Central 1' ? 'EUC' : 'USE1'}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`,
      region: batchForm.region,
      deviceModel: batchForm.deviceModel,
      firmwareVersion: batchPackage?.firmwareVersion || 'Selected package',
      status: 'Ready',
      total: batchCount,
      queued: batchCount,
      active: 0,
      complete: 0,
      failed: 0,
      updated: 'Just now',
    };
    setBatches((current) => [newBatch, ...current]);
    setBatchOpen(false);
    setBatchForm(INITIAL_BATCH_FORM);
    setNotice(`${newBatch.id} was created and is ready to start.`);
  };

  const startBatch = (batch) => {
    setBatches((current) => current.map((item) => (
      item.id === batch.id ? { ...item, status: 'In progress', active: item.queued, queued: 0, updated: 'Just now' } : item
    )));
    setNotice(`${batch.id} was started.`);
  };

  const stopBatch = (batch) => {
    setBatches((current) => current.map((item) => (
      item.id === batch.id ? { ...item, status: 'Stopped', active: 0, updated: 'Just now' } : item
    )));
    setNotice(`${batch.id} was stopped.`);
  };

  if (!canView) {
    return <div className="page"><div className="alert alert-warning">Firmware Management is not available for your role.</div></div>;
  }

  return (
    <div className="page firmware-page">
      <div className="firmware-header"><div><h2>Firmware Management</h2><p className="muted">Manage packages, release availability, device updates, and controlled rollouts.</p></div><span className="firmware-role-badge">{activeRole}</span></div>
      {notice ? <div className="alert alert-success firmware-notice">{notice}<button type="button" onClick={() => setNotice('')} aria-label="Dismiss notification">x</button></div> : null}

      <div className="firmware-tabs" role="tablist" aria-label="Firmware management views">
        <button type="button" className={activeTab === 'packages' ? 'active' : ''} onClick={() => setActiveTab('packages')} role="tab" aria-selected={activeTab === 'packages'}>Packages</button>
        <button type="button" className={activeTab === 'devices' ? 'active' : ''} onClick={() => setActiveTab('devices')} role="tab" aria-selected={activeTab === 'devices'}>Device Updates</button>
        <button type="button" className={activeTab === 'batches' ? 'active' : ''} onClick={() => setActiveTab('batches')} role="tab" aria-selected={activeTab === 'batches'}>Batch Rollouts</button>
      </div>

      {activeTab === 'packages' ? <FirmwarePackagesTab packages={packages} filteredPackages={filteredPackages} packageSearch={packageSearch} packageChannel={packageChannel} packagesLoading={packagesLoading} packagesError={packagesError} packagesNextToken={packagesNextToken} activeRollouts={batches.filter((batch) => batch.status === 'In progress').length} canUpload={canUpload} canRelease={canRelease} canRemove={canRemove} onPackageSearchChange={setPackageSearch} onPackageChannelChange={setPackageChannel} onResetFilters={() => { setPackageSearch(''); setPackageChannel('ALL'); }} onLoadMore={() => loadPackages({ nextToken: packagesNextToken, append: true })} onRetryChannels={retryChannels} onOpenUpload={() => { setUploadError(''); setUploadOpen(true); }} onViewPackage={openPackageDetails} onPromotePackage={setPromotionPackage} onOpenGroups={() => setGroupsOpen(true)} onRemovePackage={(firmware) => { setRemoveError(''); setRemovalPackage(firmware); }} /> : null}
      {activeTab === 'devices' ? <FirmwareDeviceUpdatesTab canRequestDeviceUpdate={canRequestDeviceUpdate} deviceQuery={deviceQuery} childDeviceId={childDeviceId} deviceRegionId={deviceRegionId} selectedDevice={selectedDevice} deviceChannel={deviceChannel} deviceSearchPerformed={deviceSearchPerformed} searchingDevice={searchingDevice} deviceSearchError={deviceSearchError} upgradeError={upgradeError} availableFirmware={availableFirmware} compatibleUpdate={compatibleUpdate} checking={checking} upgradeSubmitted={upgradeResult.some((item) => item.update)} onDeviceQueryChange={setDeviceQuery} onChildDeviceIdChange={setChildDeviceId} onDeviceRegionChange={setDeviceRegionId} onDeviceSearch={searchDevice} onDeviceChannelChange={changeDeviceChannel} onCheckCompatibility={checkCompatibility} onOpenInstall={() => { setInstallReason(''); setInstallOpen(true); }} /> : null}
      {activeTab === 'batches' ? <FirmwareBatchRolloutsTab batches={batches} canRequestDeviceUpdate={canRequestDeviceUpdate} onOpenBatch={() => setBatchOpen(true)} onShowDetails={setBatchDetails} onStartBatch={startBatch} onStopBatch={stopBatch} /> : null}

      <UploadFirmwareDialog open={uploadOpen} form={uploadForm} file={firmwareFile} error={uploadError} uploading={uploading} onClose={() => setUploadOpen(false)} onSubmit={submitUpload} onFieldChange={updateUploadForm} onFileChange={setFirmwareFile} />
      <FirmwareDetailsDialog firmware={selectedPackage} detailsLoading={packageDetailsLoading} detailsError={packageDetailsError} onRetryDetails={() => setPackageDetailsReload((value) => value + 1)} releaseNotes={releaseNotes} releaseNotesLoading={releaseNotesLoading} releaseNotesError={releaseNotesError} releaseNoteLoadingLanguage={releaseNoteLoadingLanguage} releaseNoteSaving={releaseNoteSaving} releaseNoteDeletingLanguage={releaseNoteDeletingLanguage} changeLogs={changeLogs} changeLogsLoading={changeLogsLoading} changeLogsError={changeLogsError} changeLogLoadingLanguage={changeLogLoadingLanguage} changeLogSaving={changeLogSaving} changeLogDeletingLanguage={changeLogDeletingLanguage} canManageReleaseNotes={canRelease} canManageChangeLogs={canRelease} onClose={() => setSelectedPackage(null)} onLoadReleaseNote={loadReleaseNote} onSaveReleaseNote={saveReleaseNote} onDeleteReleaseNote={removeReleaseNote} onLoadChangeLog={loadChangeLog} onSaveChangeLog={saveChangeLog} onDeleteChangeLog={removeChangeLog} />
      {groupsOpen ? <FirmwareGroupsDialog idToken={idToken} onClose={() => setGroupsOpen(false)} onComplete={setNotice} /> : null}
      {promotionPackage ? <FirmwarePromotionDialog key={promotionPackage.id} firmware={promotionPackage} idToken={idToken} promotedBy={user?.sub} onPromotionsUpdated={(firmwareId, promotions) => setPackages((current) => current.map((item) => item.id === firmwareId ? { ...item, promotions, promotionsError: '' } : item))} onClose={() => setPromotionPackage(null)} onPromoted={(promotion) => { setPackages((current) => current.map((item) => item.id === promotion.firmwareId ? { ...item, promotionsError: '', promotions: [promotion, ...(item.promotions || []).filter((entry) => entry.toChannel !== promotion.toChannel)] } : item)); setNotice(`Firmware promoted to ${promotion.toChannel}.`); }} /> : null}
      <RemoveFirmwareDialog firmware={removalPackage} removing={removing} error={removeError} onClose={() => setRemovalPackage(null)} onConfirm={removePackage} />
      <InstallFirmwareDialog open={installOpen} firmware={compatibleUpdate} device={selectedDevice} channel={deviceChannel} reason={installReason} requesting={requesting} onClose={() => setInstallOpen(false)} onReasonChange={setInstallReason} onSubmit={requestUpgrade} />
      <NewBatchRolloutDialog open={batchOpen} form={batchForm} packages={packages} batchCount={batchCount} onClose={() => setBatchOpen(false)} onSubmit={createBatch} onChange={(field, value) => setBatchForm((current) => ({ ...current, [field]: value }))} />
      <BatchDetailsDialog batch={batchDetails} onClose={() => setBatchDetails(null)} />
    </div>
  );
}
